import type { SceneController, SectionId } from '../scenes/controller';
import { mount as mountHome } from './home';
import { mount as mountGallery } from './gallery';
import { mount as mountContact } from './contact';

type MountFn = (container: HTMLElement) => () => void;

const PAGES: Record<SectionId, MountFn> = {
  home: mountHome,
  gallery: mountGallery,
  contact: mountContact,
};

function hashToSection(hash: string): SectionId {
  if (!hash || hash === '#' || hash === '#/') return 'home';
  if (hash.startsWith('#/gallery')) return 'gallery';
  if (hash === '#/contact') return 'contact';
  return 'home';
}

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export function createRouter(container: HTMLElement, scene: SceneController): () => void {
  let currentSection: SectionId | null = null;
  let cleanup: (() => void) | null = null;

  function navigate(): void {
    const section = hashToSection(location.hash);

    // Same section: page module handles sub-navigation via its own hashchange listener.
    if (section === currentSection) return;

    const mountFn = PAGES[section];
    const isInitial = currentSection === null;

    const swap = (): void => {
      cleanup?.();
      container.innerHTML = '';
      window.scrollTo(0, 0);
      container.scrollTop = 0;
      cleanup = mountFn(container);
      currentSection = section;
      scene.setActiveSection(section);
    };

    if (!isInitial && !reducedMotion && 'startViewTransition' in document) {
      void document.startViewTransition(swap);
    } else {
      swap();
    }
  }

  window.addEventListener('hashchange', navigate);
  navigate();

  return () => {
    window.removeEventListener('hashchange', navigate);
    cleanup?.();
  };
}
